// PublisherBankDetails.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    UncontrolledTooltip,
    Toast,
} from "reactstrap";
import {
    FaPlus,
    FaEdit,
    FaEye,
    FaCheck,
    FaTimes,
} from "react-icons/fa";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactBsTable from "components/Tables/ReactBsTable";
import NotificationAlert from "react-notification-alert";
import { showAlert } from "components/Common/commonFunctions";
import { useNavigate } from "react-router-dom";
import { publisherBankDetailsList, updateBillingInfoStatus } from "actions/publisher";

// =============== MODAL STYLES ===============
const modalStyles = {
    header: {
        borderBottom: "2px solid #f0f0f0",
        paddingBottom: "18px",
        paddingTop: "18px",
    },
    title: {
        fontSize: "18px",
        fontWeight: "700",
        color: "#2c3e50",
        letterSpacing: "0.3px",
    },
    body: {
        padding: "24px",
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
    },
};

// =============== DUMMY DATA ===============
const DUMMY_PUBLISHERS = [
    {
        id: 1,
        name: "MediaMax Advertising",
        legalName: "MediaMax Advertising FZ-LLC",
        country: "UAE",
        trn: "100123456789003",
        paymentTerms: "Net 30",
        currency: "USD",
        email: "finance@mediamax.ae",
    },
    {
        id: 2,
        name: "Digital Reach Network",
        legalName: "Digital Reach Network LLC",
        country: "UAE",
        trn: "100987654321001",
        paymentTerms: "Net 45",
        currency: "USD",
        email: "accounts@digitalreach.com",
    },
    {
        id: 3,
        name: "Global Ad Solutions",
        legalName: "Global Ad Solutions Pte Ltd",
        country: "Singapore",
        trn: "200912345M",
        paymentTerms: "Net 15",
        currency: "USD",
        email: "finance@globalads.sg",
    },
    {
        id: 4,
        name: "EuroMedia Partners",
        legalName: "EuroMedia Partners GmbH",
        country: "Germany",
        trn: "DE123456789",
        paymentTerms: "Net 60",
        currency: "USD",
        email: "billing@euromedia.de",
    },
    {
        id: 5,
        name: "Asian Ad Network",
        legalName: "Asian Ad Network Ltd",
        country: "India",
        trn: "22ABCDE1234F1Z5",
        paymentTerms: "Net 30",
        currency: "USD",
        email: "accounts@asianad.in",
    },
];

// Generate dummy advance data
const generateDummyAdvances = () => {
    const months = [
        "2025-12", "2026-01", "2026-02", "2026-03", "2026-04", "2026-05"
    ];
    const publishers = DUMMY_PUBLISHERS;
    const advances = [];
    let idCounter = 1;

    publishers.forEach((publisher, pIdx) => {
        const numAdvances = Math.floor(Math.random() * 3) + 1;
        for (let i = 0; i < numAdvances; i++) {
            const monthIndex = Math.floor(Math.random() * months.length);
            const amount = Math.floor(Math.random() * 10000) + 1000;
            const usedAmount = Math.floor(Math.random() * amount);
            const remainingAmount = amount - usedAmount;

            advances.push({
                id: idCounter++,
                publisherId: publisher.id,
                publisherName: publisher.name,
                month: months[monthIndex],
                monthDisplay: format(new Date(`${months[monthIndex]}-01`), "MMMM yyyy"),
                amount: amount,
                usedAmount: usedAmount,
                remainingAmount: remainingAmount,
                currency: "USD",
                date: `${months[monthIndex]}-15`,
                description: `Advance payment for ${format(new Date(`${months[monthIndex]}-01`), "MMMM yyyy")} campaign`,
                status: remainingAmount > 0 ? "partial" : "used",
                createdBy: "Finance Admin",
                createdAt: `${months[monthIndex]}-10 10:30:00`,
            });
        }
    });

    return advances;
};

const DUMMY_ADVANCES = generateDummyAdvances();

// =============== COMPONENTS ===============
const PublisherBankDetails = () => {
    const notificationAlertRef = useRef(null);
    const [advances, setAdvances] = useState(DUMMY_ADVANCES);
    const [bankDetails, setBankDetails] = useState([]);
    const [historyLoader, setHistoryLoader] = useState(false);
    const navigate = useNavigate();
    const [rejectReason, setRejectReason] = useState("");
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [selectedBankForReject, setSelectedBankForReject] = useState(null);
    const [rejectError, setRejectError] = useState("");

    // State
    const [filteredAdvances, setFilteredAdvances] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState("2026-05");
    const [filterText, setFilterText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAdvance, setSelectedAdvance] = useState(null);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Modal states
    const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    // Form state
    const [advanceForm, setAdvanceForm] = useState({
        publisherId: "",
        amount: "",
        month: new Date(),
        description: "",
        currency: "USD",
    });

    const [editForm, setEditForm] = useState({
        id: null,
        publisherId: "",
        amount: "",
        month: new Date(),
        description: "",
        currency: "USD",
    });

    // Months dropdown
    const availableMonths = [...new Set(advances.map(a => a.month))].sort().reverse();

    // Stats
    const [stats, setStats] = useState({
        totalAdvances: 0,
        totalAmount: 0,
        totalUsed: 0,
        totalRemaining: 0,
        totalPublishers: 0,
    });

    // Get user ID from localStorage
    const userData = JSON.parse(localStorage.getItem("p") || "{}");
    const currentUserId = userData?.role || "SUPER_ADMIN";

    // =============== NOTIFICATIONS ===============
    const notify = (type, message) => {
        notificationAlertRef.current.notificationAlert({
            place: "tr",
            message: <div>{message}</div>,
            type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 5,
        });
    };

    // =============== FETCH BANK DETAILS ===============
    const fetchBankDetails = async () => {
        setHistoryLoader(true);
        try {
            const res = await publisherBankDetailsList();
         
            if (res?.code === 200 && res?.data?.PublisherBillingInfo) {
                const data = res.data.PublisherBillingInfo;
                
                const mappedData = data.map(item => ({
                    id: item.id,
                    legalName: item.legalName || "-",
                    companyName: item.companyName || item.publisherName || "-",
                    recipientName: item.accountName || "-",
                    bankName: item.bankName || "-",
                    accountNumber: item.accountNumber || "-",
                    swiftCode: item.swiftCode || "-",
                    status: item.status || "INACTIVE",
                    approvalStatus: item.approvalStatus || "PENDING",
                    billingAddress: item.billingAddress || "-",
                    countryId: item.countryId,
                    countryName: item.countryName || "-",
                    vatNumber: item.vatNumber || "-",
                    paymentCurrency: item.paymentCurrency || "-",
                    paymentTerms: item.paymentTerms || "-",
                    minimumPayout: item.minimumPayout || 0,
                    iban: item.iban || "-",
                    ifscCode: item.ifscCode || "-",
                    invoiceEmail: item.invoiceEmail || "-",
                    taxResidency: item.taxResidency || "-",
                    createdAt: item.createdAt || "-",
                    updatedAt: item.updatedAt || "-",
                    publisherID: item.publisherID || item.publisherId,
                }));
                
                setBankDetails(mappedData);
            } else {
                setBankDetails([]);
              
            }
        } catch (error) {
            console.error("Error fetching bank details:", error);
            setBankDetails([]);
            notify("danger", "Failed to fetch bank details");
        } finally {
            setHistoryLoader(false);
        }
    };

    useEffect(() => {
        fetchBankDetails();
    }, []);

    // =============== UPDATE APPROVAL STATUS ===============
    const updateApprovalStatus = async (bankId, status, bank, reason = null) => {
        try {
            setHistoryLoader(true);
            
            const payload = {
                approvalStatus: status,
                approvedBy: currentUserId,
                approvedAt: new Date().toISOString(),
                publisherID: bank?.publisherID || bank?.publisherId,
                rejectReason: status === "REJECTED" ? reason : null,
            };            
            const res = await updateBillingInfoStatus(bankId, payload);
            if (res?.code === 200) {
                setBankDetails(prev =>
                    prev.map(item =>
                        item.id === bankId
                            ? { 
                                ...item, 
                                approvalStatus: status,
                                approvedBy: currentUserId,
                                approvedAt: new Date().toISOString()
                              }
                            : item
                    )
                );
                
                const message = status === "APPROVED" 
                    ? `Bank details for "${bank?.legalName || bank?.companyName}" approved successfully!`
                    : `Bank details for "${bank?.legalName || bank?.companyName}" rejected!`;
                
                notify("success", message);
                
                // Reset rejection state
                setRejectReason("");
                setRejectModalOpen(false);
                setSelectedBankForReject(null);
                setRejectError("");
                
                // Refresh the list after successful update
                await fetchBankDetails();
            } else {
                const errorMsg = res?.message || `Failed to ${status.toLowerCase()} bank details`;
                notify("danger", errorMsg);
            }
        } catch (error) {
            console.error("Error updating status:", error);
            notify("danger", "Something went wrong while updating status");
        } finally {
            setHistoryLoader(false);
        }
    };

    // =============== EFFECTS ===============
    useEffect(() => {
        filterAdvances();
        calculateStats();
    }, [advances, selectedMonth, filterText]);

    const filterAdvances = () => {
        let filtered = advances.filter(a => a.month === selectedMonth);
        if (filterText) {
            filtered = filtered.filter(a =>
                a.publisherName.toLowerCase().includes(filterText.toLowerCase())
            );
        }
        setFilteredAdvances(filtered);
    };

    const calculateStats = () => {
        const monthAdvances = advances.filter(a => a.month === selectedMonth);
        const totalAmount = monthAdvances.reduce((sum, a) => sum + a.amount, 0);
        const totalUsed = monthAdvances.reduce((sum, a) => sum + a.usedAmount, 0);
        const totalRemaining = monthAdvances.reduce((sum, a) => sum + a.remainingAmount, 0);
        const uniquePublishers = new Set(monthAdvances.map(a => a.publisherId));

        setStats({
            totalAdvances: monthAdvances.length,
            totalAmount: totalAmount,
            totalUsed: totalUsed,
            totalRemaining: totalRemaining,
            totalPublishers: uniquePublishers.size,
        });
    };

    // =============== HANDLERS ===============
    const handleAddAdvance = () => {
        if (!advanceForm.publisherId || !advanceForm.amount || !advanceForm.description) {
            notify("danger", "Please fill all required fields");
            return;
        }

        const newAdvance = {
            id: advances.length + 1,
            publisherId: parseInt(advanceForm.publisherId),
            publisherName: DUMMY_PUBLISHERS.find(p => p.id === parseInt(advanceForm.publisherId))?.name || "",
            month: format(advanceForm.month, "yyyy-MM"),
            monthDisplay: format(advanceForm.month, "MMMM yyyy"),
            amount: parseFloat(advanceForm.amount),
            usedAmount: 0,
            remainingAmount: parseFloat(advanceForm.amount),
            currency: advanceForm.currency,
            date: format(advanceForm.month, "yyyy-MM-dd"),
            description: advanceForm.description,
            status: "active",
            createdBy: "Finance Admin",
            createdAt: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
        };

        setAdvances([...advances, newAdvance]);
        setIsAdvanceModalOpen(false);
        setAdvanceForm({
            publisherId: "",
            amount: "",
            month: new Date(),
            description: "",
            currency: "USD",
        });
        notify("success", "Advance payment added successfully!");
    };

    const handleEditAdvance = () => {
        if (!editForm.publisherId || !editForm.amount || !editForm.description) {
            notify("danger", "Please fill all required fields");
            return;
        }

        setAdvances(prev =>
            prev.map(a =>
                a.id === editForm.id
                    ? {
                        ...a,
                        publisherId: parseInt(editForm.publisherId),
                        publisherName: DUMMY_PUBLISHERS.find(p => p.id === parseInt(editForm.publisherId))?.name || "",
                        month: format(editForm.month, "yyyy-MM"),
                        monthDisplay: format(editForm.month, "MMMM yyyy"),
                        amount: parseFloat(editForm.amount),
                        remainingAmount: parseFloat(editForm.amount) - a.usedAmount,
                        currency: editForm.currency,
                        date: format(editForm.month, "yyyy-MM-dd"),
                        description: editForm.description,
                    }
                    : a
            )
        );

        setIsEditModalOpen(false);
        setEditForm({
            id: null,
            publisherId: "",
            amount: "",
            month: new Date(),
            description: "",
            currency: "USD",
        });
        notify("success", "Advance payment updated successfully!");
    };

    const handleSizePerPageChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setPageNum(1);
    };

    const openEditModal = (advance) => {
        setSelectedAdvance(advance);
        setEditForm({
            id: advance.id,
            publisherId: String(advance.publisherId),
            amount: String(advance.amount),
            month: new Date(advance.date),
            description: advance.description,
            currency: advance.currency,
        });
        setIsEditModalOpen(true);
    };

    const openViewModal = (advance) => {
        setSelectedAdvance(advance);
        setIsViewModalOpen(true);
    };

    // Open reject modal with reason input
    const openRejectModal = (bank) => {
        setSelectedBankForReject(bank);
        setRejectReason("");
        setRejectError("");
        setRejectModalOpen(true);
    };

    // Confirm reject with reason
    const confirmReject = () => {
        if (!rejectReason.trim()) {
            setRejectError("Rejection reason is required");
            return;
        }
        setRejectError("");
        updateApprovalStatus(selectedBankForReject.id, "REJECTED", selectedBankForReject, rejectReason);
    };

    // Handle approve with confirmation
    const handleApprove = async (bank, e) => {
        if (e) {
            e.stopPropagation();
        }
        showAlert({
            title: `Approve Bank Details`,
            message: `Are you sure you want to approve the bank details for "${bank.legalName || bank.companyName}"?`,
            type: "success",
            confirmText: "Yes, Approve",
            cancelText: "Cancel",
            onConfirm: () => updateApprovalStatus(bank.id, "APPROVED", bank),
            onCancel: () => {}
        });
    };

    const handleRowClick = (row) => {
        if (row && row.id) {
            navigate("/admin/publisher_bank/details", { state: row });
        }
    };

    // =============== RENDER HELPERS ===============
    const getStatusBadge = (status) => {
        const statusMap = {
            ACTIVE: { color: "success", label: "Active" },
            INACTIVE: { color: "secondary", label: "Inactive" },
            active: { color: "success", label: "Active" },
            partial: { color: "warning", label: "Partial" },
            used: { color: "secondary", label: "Used" },
            PENDING: { color: "warning", label: "Pending" },
            APPROVED: { color: "success", label: "Approved" },
            REJECTED: { color: "danger", label: "Rejected" },
        };
        const s = statusMap[status] || statusMap.PENDING;
        return <Badge color={s.color}>{s.label}</Badge>;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
    };

    // =============== BANK DETAILS TABLE COLUMNS ===============
    const bankColumns = [
        {
            dataField: "legalName",
            text: "Legal Name",
            sort: true,
        },
        {
            dataField: "companyName",
            text: "Publisher",
            sort: true,
        },
        {
            dataField: "bankName",
            text: "Bank Name",
            sort: true,
        },
        {
            dataField: "accountNumber",
            text: "Account Number",
            sort: true,
        },
        {
            dataField: "swiftCode",
            text: "SWIFT/BIC",
            sort: true,
        },
        {
            dataField: "approvalStatus",
            text: "Approval Status",
            formatter: (cell) => getStatusBadge(cell),
            sort: true,
        },
        {
            text: "Actions",
            dataField: "actions",
            headerAlign: "center",
            formatter: (cell, row) => {
                if (row.approvalStatus === "PENDING") {
                    return (
                        <div
                            className="d-flex justify-content-center"
                            style={{ gap: "8px" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Button
                                size="sm"
                                color="success"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleApprove(row, e);
                                }}
                                disabled={historyLoader}
                            >
                                <FaCheck className="mr-1" />
                                Approve
                            </Button>
                            <Button
                                size="sm"
                                className="btn-gradient-primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openRejectModal(row);
                                }}
                                disabled={historyLoader}
                            >
                                <FaTimes className="mr-1" />
                                Reject
                            </Button>
                        </div>
                    );
                }
                return getStatusBadge(row.approvalStatus);
            },
            align: "center",
            headerAlign: "center",
        }
    ];

    // =============== RENDER ===============
    return (
        <>
            <div className="rna-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
            <Container className="mt--6" fluid>
                {/* Bank Details Table */}
                <Card>
                    <ReactBsTable
                        title="Bank Details Verification"
                        description="Review and verify publisher bank details"
                        data={bankDetails}
                        columns={bankColumns}
                        keyField="id"
                        page={1}
                        sizePerPage={10}
                        totalRecords={bankDetails.length}
                        confirmBtnBsStyle="info"
                        showLoader={historyLoader}
                        bordered
                        hover
                        enablePageSizeManagement={false}
                        showAddbtn={false}
                        showColumnsBtn={false}
                        exportCSVBtnShow={false}
                        handleRowClick={handleRowClick}
                    />
                </Card>

                {/* ========== MODALS ========== */}

                {/* Add Advance Modal */}
                <Modal isOpen={isAdvanceModalOpen} toggle={() => setIsAdvanceModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsAdvanceModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>
                            Add Advance Payment
                        </h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Select Publisher <span className="text-danger">*</span></Label>
                                        <Input
                                            type="select"
                                            value={advanceForm.publisherId}
                                            onChange={(e) => setAdvanceForm({ ...advanceForm, publisherId: e.target.value })}
                                        >
                                            <option value="">Select Publisher</option>
                                            {DUMMY_PUBLISHERS.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Month <span className="text-danger">*</span></Label>
                                        <DatePicker
                                            selected={advanceForm.month}
                                            onChange={(date) => setAdvanceForm({ ...advanceForm, month: date })}
                                            className="form-control"
                                            dateFormat="MMMM yyyy"
                                            showMonthYearPicker
                                            placeholderText="Select Month"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Amount (USD) <span className="text-danger">*</span></Label>
                                        <Input
                                            type="number"
                                            placeholder="Enter amount in USD"
                                            value={advanceForm.amount}
                                            onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <FormGroup>
                                <Label>Description <span className="text-danger">*</span></Label>
                                <Input
                                    type="textarea"
                                    placeholder="Enter description..."
                                    value={advanceForm.description}
                                    onChange={(e) => setAdvanceForm({ ...advanceForm, description: e.target.value })}
                                    rows={3}
                                />
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsAdvanceModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            className="filter-btn-gradient-primary btn-md responsive"
                            onClick={handleAddAdvance}
                            disabled={!advanceForm.publisherId || !advanceForm.amount || !advanceForm.description}
                        >
                            Add Advance
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Edit Advance Modal */}
                <Modal isOpen={isEditModalOpen} toggle={() => setIsEditModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsEditModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>
                            <FaEdit className="me-2 text-primary" /> Edit Advance Payment
                        </h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Select Publisher <span className="text-danger">*</span></Label>
                                        <Input
                                            type="select"
                                            value={editForm.publisherId}
                                            onChange={(e) => setEditForm({ ...editForm, publisherId: e.target.value })}
                                        >
                                            <option value="">Select Publisher</option>
                                            {DUMMY_PUBLISHERS.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Month <span className="text-danger">*</span></Label>
                                        <DatePicker
                                            selected={editForm.month}
                                            onChange={(date) => setEditForm({ ...editForm, month: date })}
                                            className="form-control"
                                            dateFormat="MMMM yyyy"
                                            showMonthYearPicker
                                            placeholderText="Select Month"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Amount (USD) <span className="text-danger">*</span></Label>
                                        <Input
                                            type="number"
                                            placeholder="Enter amount in USD"
                                            value={editForm.amount}
                                            onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <FormGroup>
                                <Label>Description <span className="text-danger">*</span></Label>
                                <Input
                                    type="textarea"
                                    placeholder="Enter description..."
                                    value={editForm.description}
                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                    rows={3}
                                />
                            </FormGroup>
                            {selectedAdvance && (
                                <Alert color="info">
                                    <strong>Current Used Amount:</strong> {formatCurrency(selectedAdvance.usedAmount)}
                                    <br />
                                    <strong>Current Remaining:</strong> {formatCurrency(selectedAdvance.remainingAmount)}
                                </Alert>
                            )}
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsEditModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            className="filter-btn-gradient-primary btn-md responsive"
                            onClick={handleEditAdvance}
                            disabled={!editForm.publisherId || !editForm.amount || !editForm.description}
                        >
                            Update Advance
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* View Advance Modal */}
                <Modal isOpen={isViewModalOpen} toggle={() => setIsViewModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsViewModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>Advance Details</h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        {selectedAdvance && (
                            <>
                                <Row className="px-3">
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Publisher</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.publisherName}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Month</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.monthDisplay}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="px-3">
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Amount</Label>
                                            <span className="font-weight-bold">
                                                {formatCurrency(selectedAdvance.amount)}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Used Amount</Label>
                                            <span className="font-weight-bold text-warning">
                                                {formatCurrency(selectedAdvance.usedAmount)}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="px-3">
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Remaining Amount</Label>
                                            <span className="font-weight-bold text-success">
                                                {formatCurrency(selectedAdvance.remainingAmount)}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Status</Label>
                                            <span>
                                                {getStatusBadge(selectedAdvance.status)}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="px-3">
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Date</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.date}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Created By</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.createdBy}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="px-3">
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Created At</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.createdAt}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="px-3">
                                    <Col md={12} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Description</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.description}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsViewModalOpen(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>

            {/* Custom Reject Modal */}
            <Modal
                isOpen={rejectModalOpen}
                toggle={() => {
                    setRejectModalOpen(false);
                    setRejectReason("");
                    setRejectError("");
                    setSelectedBankForReject(null);
                }}
                centered
                size="md"
            >
                <ModalHeader
                    toggle={() => {
                        setRejectModalOpen(false);
                        setRejectReason("");
                        setRejectError("");
                        setSelectedBankForReject(null);
                    }}
                    style={modalStyles.header}
                >
                    <h5 style={modalStyles.title}>Reject Bank Details</h5>
                </ModalHeader>
                <ModalBody style={modalStyles.body}>
                    <p>
                        Are you sure you want to reject the bank details for "
                        <strong>{selectedBankForReject?.legalName || selectedBankForReject?.companyName}</strong>"?
                    </p>
                    <FormGroup className="mt-3">
                        <Label for="rejectReason" className="font-weight-bold">
                            Rejection Reason <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            id="rejectReason"
                            name="rejectReason"
                            placeholder="Please enter the reason for rejection..."
                            value={rejectReason}
                            onChange={(e) => {
                                setRejectReason(e.target.value);
                                if (rejectError) setRejectError("");
                            }}
                            rows="4"
                            className={rejectError ? "is-invalid" : ""}
                            autoFocus
                        />
                        {rejectError && <div className="text-danger small mt-1">{rejectError}</div>}
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button
                        className="btn-gradient-primary"
                        onClick={() => {
                            setRejectModalOpen(false);
                            setRejectReason("");
                            setRejectError("");
                            setSelectedBankForReject(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="secondary"
                        onClick={confirmReject}
                       
                        disabled={historyLoader}
                    >
                        {historyLoader ? (
                            <>
                                <i className="fa fa-spinner fa-spin mr-2" />
                                Processing...
                            </>
                        ) : (
                            "Yes, Reject"
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default PublisherBankDetails;